import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { clientsAPI } from '../../lib/clientsAPI';
import { commsAPI, errMsg, notifyCommsChanged } from '../../lib/communicationsAPI';

export const StartConversationDialog = ({ open, onClose, onCreated, defaultClientId, defaultSubject, defaultBody }) => {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ client_id: '', subject: '', body: '', urgent: false });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) clientsAPI.list().then((r) => setClients(r.data || [])).catch(() => toast.error('Failed to load clients'));
    if (open && defaultClientId) setForm((f) => ({ ...f, client_id: defaultClientId }));
    if (open && (defaultSubject || defaultBody)) setForm((f) => ({ ...f, subject: defaultSubject || f.subject, body: defaultBody || f.body }));
  }, [open, defaultClientId, defaultSubject, defaultBody]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.client_id || !form.subject.trim() || !form.body.trim()) { toast.error('Client, subject and message are required'); return; }
    setBusy(true);
    try {
      const res = await commsAPI.create(form);
      toast.success('Conversation started — sent via Client Portal');
      notifyCommsChanged();
      setForm({ client_id: '', subject: '', body: '', urgent: false });
      onCreated(res.data.conversation.id);
    } catch (err) { toast.error(errMsg(err, 'Failed to start conversation')); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="comms-start-dialog">
        <DialogHeader>
          <DialogTitle>New conversation</DialogTitle>
          <DialogDescription>The client will see this in their portal Messages.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>Client</Label>
            <Select value={form.client_id} onValueChange={set('client_id')}>
              <SelectTrigger data-testid="comms-start-client-select"><SelectValue placeholder="Choose a client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.display_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Subject</Label>
            <Input value={form.subject} onChange={(e) => set('subject')(e.target.value)} maxLength={200} data-testid="comms-start-subject" />
          </div>
          <div className="space-y-1.5">
            <Label>Message</Label>
            <Textarea rows={5} value={form.body} onChange={(e) => set('body')(e.target.value)} data-testid="comms-start-body" />
          </div>
          <div className="flex items-center justify-between">
            <Label>Urgent</Label>
            <Switch checked={form.urgent} onCheckedChange={set('urgent')} data-testid="comms-start-urgent" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="comms-start-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="comms-start-submit">{busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Send via Client Portal</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
